import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiEdit2, FiTrash2, FiEye } from 'react-icons/fi';
import EmbedCodeGenerator from '../components/EmbedCodeGenerator';

function FormEditor({ form, onSave, onClose }) {
  const [editForm, setEditForm] = useState(form);

  const addPackage = () => {
    const newPackage = {
      id: editForm.packages.length + 1,
      name: 'New Package',
      description: 'Package Description',
      price: 0,
      originalPrice: 0
    };
    setEditForm({
      ...editForm,
      packages: [...editForm.packages, newPackage]
    });
  };

  const addField = () => {
    const newField = {
      id: `field_${editForm.fields.length + 1}`,
      label: 'New Field',
      type: 'text',
      required: true
    };
    setEditForm({
      ...editForm,
      fields: [...editForm.fields, newField]
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">Edit Form: {editForm.name}</h2>

        <div className="mb-6">
          <label className="block mb-2 font-semibold">Form Name</label>
          <input
            type="text"
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            className="w-full p-2 border rounded"
          />
        </div>

        <div className="mb-6">
          <label className="block mb-2 font-semibold">Thank You Page URL</label>
          <input
            type="text"
            value={editForm.thankYouUrl}
            onChange={(e) => setEditForm({ ...editForm, thankYouUrl: e.target.value })}
            className="w-full p-2 border rounded"
          />
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Packages</h3>
            <button
              onClick={addPackage}
              className="bg-green-500 text-white px-3 py-1 rounded text-sm"
            >
              Add Package
            </button>
          </div>
          {editForm.packages.map((pkg, index) => (
            <div key={pkg.id} className="mb-4 p-4 border rounded">
              <div className="grid grid-cols-2 gap-4">
                <input
                  placeholder="Package Name"
                  value={pkg.name}
                  onChange={(e) => {
                    const newPackages = [...editForm.packages];
                    newPackages[index] = { ...pkg, name: e.target.value };
                    setEditForm({ ...editForm, packages: newPackages });
                  }}
                  className="p-2 border rounded"
                />
                <input
                  placeholder="Description"
                  value={pkg.description}
                  onChange={(e) => {
                    const newPackages = [...editForm.packages];
                    newPackages[index] = { ...pkg, description: e.target.value };
                    setEditForm({ ...editForm, packages: newPackages });
                  }}
                  className="p-2 border rounded"
                />
                <input
                  type="number"
                  placeholder="Price"
                  value={pkg.price}
                  onChange={(e) => {
                    const newPackages = [...editForm.packages];
                    newPackages[index] = { ...pkg, price: Number(e.target.value) };
                    setEditForm({ ...editForm, packages: newPackages });
                  }}
                  className="p-2 border rounded"
                />
                <input
                  type="number"
                  placeholder="Original Price"
                  value={pkg.originalPrice}
                  onChange={(e) => {
                    const newPackages = [...editForm.packages];
                    newPackages[index] = { ...pkg, originalPrice: Number(e.target.value) };
                    setEditForm({ ...editForm, packages: newPackages });
                  }}
                  className="p-2 border rounded"
                />
              </div>
              <button
                onClick={() => {
                  const newPackages = editForm.packages.filter((_, i) => i !== index);
                  setEditForm({ ...editForm, packages: newPackages });
                }}
                className="mt-2 text-red-500 text-sm"
              >
                Remove Package
              </button>
            </div>
          ))}
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Fields</h3>
            <button
              onClick={addField}
              className="bg-green-500 text-white px-3 py-1 rounded text-sm"
            >
              Add Field
            </button>
          </div>
          {editForm.fields.map((field, index) => (
            <div key={field.id} className="mb-4 p-4 border rounded">
              <div className="grid grid-cols-2 gap-4">
                <input
                  placeholder="Field Label"
                  value={field.label}
                  onChange={(e) => {
                    const newFields = [...editForm.fields];
                    newFields[index] = { ...field, label: e.target.value };
                    setEditForm({ ...editForm, fields: newFields });
                  }}
                  className="p-2 border rounded"
                />
                <select
                  value={field.type}
                  onChange={(e) => {
                    const newFields = [...editForm.fields];
                    newFields[index] = { ...field, type: e.target.value };
                    setEditForm({ ...editForm, fields: newFields });
                  }}
                  className="p-2 border rounded"
                >
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                  <option value="tel">Phone</option>
                  <option value="textarea">Textarea</option>
                </select>
              </div>
              <div className="mt-2">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) => {
                      const newFields = [...editForm.fields];
                      newFields[index] = { ...field, required: e.target.checked };
                      setEditForm({ ...editForm, fields: newFields });
                    }}
                    className="mr-2"
                  />
                  Required
                </label>
              </div>
              <button
                onClick={() => {
                  const newFields = editForm.fields.filter((_, i) => i !== index);
                  setEditForm({ ...editForm, fields: newFields });
                }}
                className="mt-2 text-red-500 text-sm"
              >
                Remove Field
              </button>
            </div>
          ))}
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Embed Form</h3>
          <EmbedCodeGenerator formId={editForm.id} />
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(editForm)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [forms, setForms] = useState([
    {
      id: 1,
      name: 'Order Form 1',
      submissions: 12,
      packages: [
        { id: 1, name: 'PAKEJ A', description: '1 Kotak', price: 60, originalPrice: 70 },
        { id: 2, name: 'PAKEJ B', description: '2 Kotak', price: 100, originalPrice: 240 },
      ],
      fields: [
        { id: 'name', label: 'Nama', type: 'text', required: true },
        { id: 'phone', label: 'No Phone', type: 'tel', required: true },
        { id: 'address', label: 'Alamat', type: 'textarea', required: true },
      ],
      thankYouUrl: '/terima-kasih-1'
    },
  ]);

  const [showFormEditor, setShowFormEditor] = useState(false);
  const [editingForm, setEditingForm] = useState(null);

  const handleEditForm = (form) => {
    setEditingForm(form);
    setShowFormEditor(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Forms Dashboard</h1>
        <button 
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          onClick={() => {
            const newForm = {
              id: forms.length + 1,
              name: `Order Form ${forms.length + 1}`,
              submissions: 0,
              packages: [],
              fields: [
                { id: 'name', label: 'Nama', type: 'text', required: true },
                { id: 'phone', label: 'No Phone', type: 'tel', required: true },
              ],
              thankYouUrl: '/terima-kasih'
            };
            setForms([...forms, newForm]);
            handleEditForm(newForm);
          }}
        >
          Create New Form
        </button>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {forms.map((form) => (
          <motion.div
            key={form.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-lg shadow-md"
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-semibold">{form.name}</h2>
              <div className="flex space-x-2">
                <button 
                  onClick={() => handleEditForm(form)}
                  className="text-blue-500 hover:text-blue-600"
                >
                  <FiEdit2 size={18} />
                </button>
                <button 
                  onClick={() => setForms(forms.filter(f => f.id !== form.id))}
                  className="text-red-500 hover:text-red-600"
                >
                  <FiTrash2 size={18} />
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600">Submissions: {form.submissions}</p>
              <p className="text-gray-600">Packages: {form.packages.length}</p>
              <p className="text-gray-600">Fields: {form.fields.length}</p>
            </div>

            <div className="flex space-x-2">
              <Link
                to={`/form/${form.id}`}
                className="flex items-center justify-center bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                <FiEye className="mr-2" /> View Form
              </Link>
            </div>
          </motion.div>
        ))}
      </div>

      {showFormEditor && (
        <FormEditor 
          form={editingForm} 
          onSave={(updatedForm) => {
            setForms(forms.map(f => f.id === updatedForm.id ? updatedForm : f));
            setShowFormEditor(false);
          }}
          onClose={() => setShowFormEditor(false)}
        />
      )}
    </div>
  );
}